import type { UseQueryResult } from '@tanstack/react-query'

import {
  useGetDeliveryPreference as useGetDeliveryPreferenceFromService,
  type AppError,
} from '@dhl/receiver-preference-api'

import { useFetchServicepoint } from '@dhl/receiver-servicepoint'
import { type EnrichedDeliveryPreference } from '../model/model.js'

type EnrichedDeliveryPreferenceResponse = {
  preference: EnrichedDeliveryPreference
}

export const useGetDeliveryPreferenceForAddress = (
  addressId: string | undefined,
  options?: Parameters<typeof useGetDeliveryPreferenceFromService<EnrichedDeliveryPreferenceResponse | undefined>>[1]
): UseQueryResult<EnrichedDeliveryPreferenceResponse | undefined, AppError> => {
  const {
    data: preferenceData,
    isLoading: isLoadingPreference,
    ...preferenceQueryResult
  } = useGetDeliveryPreferenceFromService<EnrichedDeliveryPreferenceResponse | undefined>(addressId!, {
    query: {
      enabled: !!addressId,
      ...options?.query,
    },
  })

  const { data: servicepoint, isLoading: isLoadingServicepoint } = useFetchServicepoint(
    {
      countryCode: preferenceData?.preference?.address?.countryCode ?? '',
      id: preferenceData?.preference?.servicePointSettings?.psfKey ?? '',
    },
    {
      query: {
        enabled:
          preferenceData?.preference?.activeSetting?.preferenceType === 'ServicePointDelivery' &&
          !!preferenceData?.preference?.address?.countryCode &&
          !!preferenceData?.preference?.servicePointSettings?.psfKey,
      },
    }
  )

  return {
    ...preferenceQueryResult,
    isLoading: isLoadingPreference || isLoadingServicepoint,
    data: preferenceData
      ? {
          ...preferenceData,
          preference: {
            ...preferenceData.preference,
            servicepointData: servicepoint,
          },
        }
      : undefined,
  } as UseQueryResult<EnrichedDeliveryPreferenceResponse | undefined, AppError>
}
